/**
 * @created     2026-03-25
 * @description Overlay modal for contact data capture (email, phone, callback slot).
 *              Used by DEFER_CONTACT, SETTLE_CONTACT, INFO_REQUEST_CONTACT nodes.
 *              Personal data is NOT handled in the bot chat for privacy/data protection.
 *              Depends on: ChatAction modalId 'contact_form' from bot response
 */
import {
  Component,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  isDevMode,
  input,
  afterNextRender,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  CALLBACK_PERIODS,
  CALLBACK_WEEKDAYS,
  CallbackDayKey,
  CallbackPeriod,
  createCallbackAvailabilitySelection,
  formatCallbackAvailability,
  setCallbackAvailabilitySlot,
} from '../../utils/callback-availability';
import { isPlausiblePhone, isValidEmail } from '../../utils/form-validators';

@Component({
  selector: 'app-contact-form-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './contact-form-modal.component.html',
  styleUrl: './contact-form-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactFormModalComponent {
  private readonly i18n = inject(I18nService);
  private readonly injector = inject(Injector);

  /** Emits when the modal should be closed without submitting */
  @Output() close = new EventEmitter<void>();

  /** Emits the captured contact data on submit */
  @Output() submitContact = new EventEmitter<{ email: string; telefon: string; callback_time_slot: string }>();

  readonly showDevHelper = isDevMode();
  readonly phoneRequired = input(false);
  readonly callbackWeekdays = CALLBACK_WEEKDAYS;
  readonly callbackPeriods = CALLBACK_PERIODS;
  readonly email = signal('');
  readonly telefon = signal('');
  readonly submitAttempted = signal(false);
  readonly callbackExpanded = signal(false);
  readonly callbackSelection = signal(createCallbackAvailabilitySelection());
  readonly callbackTimeSlot = computed(() => formatCallbackAvailability(this.callbackSelection()));
  readonly callbackTimeSlotLabel = computed(() => this.formatCallbackAvailabilityLabel());

  readonly isValid = computed(() => {
    const emailVal = this.email().trim();
    const telefonVal = this.telefon().trim();
    const hasTelefon = telefonVal.length > 0;
    const telefonOk = !hasTelefon || isPlausiblePhone(telefonVal);

    return isValidEmail(emailVal) && (!this.phoneRequired() || hasTelefon) && telefonOk;
  });
  readonly emailValid = computed(() => isValidEmail(this.email().trim()));
  readonly telefonValid = computed(() => {
    const telefon = this.telefon().trim();
    return telefon === '' ? !this.phoneRequired() : isPlausiblePhone(telefon);
  });

  onClose(): void {
    this.close.emit();
  }

  insertTestData(): void {
    if (!this.showDevHelper) return;
    this.email.set('testdaten.kontakt@example.com');
    this.telefon.set('+49 151 23456789');
    this.callbackSelection.set(setCallbackAvailabilitySlot(
      createCallbackAvailabilitySelection(),
      'Mo',
      'Vormittag',
      true
    ));
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isValid()) return;
    this.submitContact.emit({
      email: this.email().trim(),
      telefon: this.telefon().trim(),
      callback_time_slot: this.callbackTimeSlot(),
    });
  }

  toggleCallbackExpanded(): void {
    this.callbackExpanded.update((expanded) => !expanded);
    if (this.callbackExpanded()) {
      afterNextRender(() => document.getElementById('contact-callback-options')?.scrollIntoView({ block: 'center' }), { injector: this.injector });
    }
  }

  isCallbackSlotSelected(dayKey: CallbackDayKey, period: CallbackPeriod): boolean {
    return this.callbackSelection()[dayKey][period];
  }

  onCallbackSlotChange(dayKey: CallbackDayKey, period: CallbackPeriod, event: Event): void {
    const selected = (event.target as HTMLInputElement).checked;
    this.callbackSelection.update((selection) => setCallbackAvailabilitySlot(selection, dayKey, period, selected));
  }

  callbackDayLabel(dayKey: CallbackDayKey): string {
    const key = `callbackAvailability.days.${dayKey}`;
    const label = this.i18n.t(key);
    return label === key ? dayKey : label;
  }

  callbackPeriodLabel(period: CallbackPeriod): string {
    const key = `callbackAvailability.periods.${period}`;
    const label = this.i18n.t(key);
    return label === key ? period : label;
  }

  private formatCallbackAvailabilityLabel(): string {
    return this.callbackWeekdays
      .map((day) => {
        const periods = this.callbackPeriods
          .filter((period) => this.callbackSelection()[day.key][period])
          .map((period) => this.callbackPeriodLabel(period));
        return periods.length > 0 ? `${this.callbackDayLabel(day.key)} (${periods.join(', ')})` : '';
      })
      .filter(Boolean)
      .join(', ');
  }

}
